<template>
  <div class="glass-container">
    <div class="gift-card-management">
      <!-- Main Card -->
      <div class="modern-card">
        <!-- Header Section -->
        <div class="header-section">
          <div class="title-section">
            <h1 class="page-title">
              <b-icon-gift class="title-icon" />
              Gift Card Management
            </h1>
            <p class="page-subtitle">View, search, and manage all gift cards</p>
          </div>
          
          <div class="action-buttons">
            <b-button
              variant="success"
              size="sm"
              @click="$router.push('/gift-card/generate')"
              class="action-btn add-btn"
            >
              <b-icon-plus-circle class="mr-2" />
              New Gift Card
            </b-button>
            <b-button
              @click="loadAllData"
              variant="outline-secondary"
              size="sm"
              class="action-btn refresh-btn"
            >
              <b-icon-arrow-clockwise class="mr-2" />
              Refresh
            </b-button>
          </div>
        </div>

        <!-- Stats Cards -->
        <b-row class="stats-row mb-4">
          <b-col md="3" sm="6" v-for="stat in stats" :key="stat.key">
            <div class="stat-card glass-card animate-slide-up" :style="{ animationDelay: getDelay(stat.key) }">
              <div class="stat-icon" :style="{ background: stat.color }">
                <b-icon :icon="stat.icon" />
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Filters -->
        <b-row class="filters-row mb-3">
          <b-col md="4" class="filter-item">
            <b-input-group size="sm">
              <b-form-input
                v-model="filters.search"
                placeholder="Search by code, email, or name..."
                @input="applyFilters"
                class="filter-input"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="applyFilters" class="search-btn">
                  <b-icon-search />
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="3" class="filter-item">
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              size="sm"
              @change="applyFilters"
              class="filter-select"
            />
          </b-col>
          <b-col md="3" class="filter-item">
            <b-form-select
              v-model="filters.batch"
              :options="batchOptions"
              size="sm"
              @change="applyFilters"
              class="filter-select"
            />
          </b-col>
        </b-row>

        <!-- Info Alert -->
        <b-alert variant="info" show class="info-alert">
          <div class="alert-content">
            <b-icon-info-circle class="mr-2" />
            <div>
              <strong>Important:</strong> This tool contains list of all gift cards in the system.
              You can search, filter, and manage gift cards here. Click on the eye icon to view details
              or the envelope icon to send the card via email.
            </div>
          </div>
        </b-alert>

        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <loading
            :active="loading"
            :can-cancel="false"
            :is-full-page="false"
          ></loading>
        </div>

        <!-- Gift Cards Table -->
        <div v-else class="table-wrapper">
          <b-table
            :items="giftCards"
            :fields="tableFields"
            striped
            hover
            responsive
            class="glass-table modern-table"
            :busy="loading"
          >
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.value)" class="status-badge">
                <span class="status-dot" :class="data.value"></span>
                {{ data.value }}
              </b-badge>
            </template>
            
            <template #cell(amount)="data">
              <span class="amount-value">${{ formatNumber(data.value) }}</span>
            </template>
            
            <template #cell(remaining_balance)="data">
              <span class="balance-value">${{ formatNumber(data.value) }}</span>
            </template>
            
            <template #cell(created_at)="data">
              <span class="date-value">{{ formatDate(data.value) }}</span>
            </template>
            
            <template #cell(assigned_to_email)="data">
              <span v-if="data.value" class="email-value">
                <b-icon-envelope-fill class="email-icon" />
                {{ data.value }}
              </span>
              <span v-else class="text-muted">Not assigned</span>
            </template>
            
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="outline-info"
                @click="viewCard(data.item)"
                class="action-icon-btn"
                title="View Details"
              >
                <b-icon-eye />
              </b-button>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="sendCardEmail(data.item)"
                :disabled="!data.item.assigned_to_email"
                class="action-icon-btn"
                title="Send Email"
              >
                <b-icon-envelope />
              </b-button>
            </template>
          </b-table>
          
          <!-- Empty State -->
          <div v-if="giftCards.length === 0 && !loading" class="empty-state">
            <div class="empty-state-icon">
              <b-icon-gift font-scale="3" />
            </div>
            <h3>No Gift Cards Found</h3>
            <p>No gift cards match your current filters</p>
            <div class="empty-state-actions mt-3">
              <b-button @click="resetFilters" variant="primary">
                <b-icon-arrow-counterclockwise class="mr-2" />
                Reset Filters
              </b-button>
              <b-button @click="$router.push('/gift-card/generate')" variant="success">
                <b-icon-plus-circle class="mr-2" />
                Create New
              </b-button>
            </div>
          </div>
          
          <!-- Pagination -->
          <div class="table-footer" v-if="giftCards.length > 0">
            <div class="pagination-info">
              Showing {{ giftCards.length }} of {{ totalRows }} gift cards
            </div>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              size="sm"
              class="custom-pagination"
              @change="loadGiftCards"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

export default {
  name: 'GiftCardManagement',
  components: { Loading },
  data() {
    return {
      loading: false,
      giftCards: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      stats: [
        { 
          key: 'total', 
          value: 0, 
          label: 'Total Cards', 
          icon: 'grid-3x3-gap-fill',
          color: 'linear-gradient(135deg, #667eea, #764ba2)'
        },
        { 
          key: 'active', 
          value: 0, 
          label: 'Active', 
          icon: 'check-circle-fill',
          color: 'linear-gradient(135deg, #48bb78, #38a169)'
        },
        { 
          key: 'redeemed', 
          value: 0, 
          label: 'Redeemed', 
          icon: 'check2-circle',
          color: 'linear-gradient(135deg, #4299e1, #3182ce)'
        },
        { 
          key: 'total_amount', 
          value: '$0', 
          label: 'Total Value', 
          icon: 'cash-stack',
          color: 'linear-gradient(135deg, #f6ad55, #ed8936)'
        }
      ],
      
      filters: {
        search: '',
        status: 'all',
        batch: 'all'
      },
      
      statusOptions: [
        { value: 'all', text: 'All Status' },
        { value: 'active', text: 'Active' },
        { value: 'redeemed', text: 'Redeemed' },
        { value: 'sent', text: 'Sent' },
        { value: 'expired', text: 'Expired' },
        { value: 'cancelled', text: 'Cancelled' }
      ],
      
      batchOptions: [
        { value: 'all', text: 'All Batches' }
      ],
      
      tableFields: [
        { key: 'code', label: 'Code', sortable: true },
        { key: 'amount', label: 'Amount', sortable: true },
        { key: 'remaining_balance', label: 'Balance', sortable: true },
        { key: 'assigned_to_email', label: 'Assigned To' },
        { key: 'status', label: 'Status' },
        { key: 'used_count', label: 'Uses', sortable: true },
        { key: 'created_at', label: 'Created', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-center' }
      ]
    };
  },
  
  created() {
    this.loadAllData();
  },
  
  methods: {
    getDelay(key) {
      const delays = { total: '0s', active: '0.1s', redeemed: '0.2s', total_amount: '0.3s' };
      return delays[key] || '0s';
    },
    
    formatNumber(value) {
      if (value === undefined || value === null) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    async loadAllData() {
      await Promise.all([
        this.loadGiftCards(),
        this.loadStats(),
        this.loadBatches()
      ]);
    },
    
    async loadGiftCards() {
      this.loading = true;
      
      try {
        const params = {
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage
        };
        
        if (this.filters.search) {
          params.code = this.filters.search;
        }
        
        if (this.filters.status !== 'all') {
          params.status = this.filters.status;
        }
        
        if (this.filters.batch !== 'all') {
          params.batch_id = this.filters.batch;
        }
        
        const response = await Requestor.getAsyncP('/api/giftcard/get_gift_cards/', params, this);
        
        if (response.data.result) {
          this.giftCards = response.data.result;
          this.totalRows = response.data.total || 0;
        }
      } catch (error) {
        console.error('Error loading gift cards:', error);
        this.$bvToast.toast('Failed to load gift cards', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      } finally {
        this.loading = false;
      }
    },
    
    async loadStats() {
      try {
        const response = await Requestor.getAsyncP('/api/giftcard/stats/', {}, this);
        if (response.data.result) {
          const stats = response.data.result;
          // Find and update each stat
          this.stats.forEach(stat => {
            switch(stat.key) {
              case 'total':
                stat.value = stats.total_cards || 0;
                break;
              case 'active':
                stat.value = stats.active_cards || 0;
                break;
              case 'redeemed':
                stat.value = stats.redeemed_cards || 0;
                break;
              case 'total_amount':
                stat.value = `$${this.formatNumber(stats.total_amount || 0)}`;
                break;
            }
          });
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },
    
    async loadBatches() {
      try {
        const response = await Requestor.getAsyncP('/api/giftcard/get_batches/', {}, this);
        if (response.data.result) {
          this.batchOptions = [
            { value: 'all', text: 'All Batches' },
            ...response.data.result.map(b => ({
              value: b.batch_id,
              text: b.name || `Batch ${b.batch_id}`
            }))
          ];
        }
      } catch (error) {
        console.error('Error loading batches:', error);
      }
    },
    
    applyFilters() {
      this.currentPage = 1;
      this.loadGiftCards();
    },
    
    resetFilters() {
      this.filters.search = '';
      this.filters.status = 'all';
      this.filters.batch = 'all';
      this.applyFilters();
    },
    
    getStatusVariant(status) {
      const variants = {
        'active': 'success',
        'sent': 'primary',
        'redeemed': 'secondary',
        'expired': 'danger',
        'cancelled': 'danger',
        'pending': 'warning'
      };
      return variants[status] || 'secondary';
    },
    
    viewCard(card) {
      this.$router.push(`/gift-card/view/${card.id}`);
    },
    
    async sendCardEmail(card) {
      if (!card.assigned_to_email) {
        this.$bvToast.toast('No email address assigned to this card', {
          title: 'Info',
          variant: 'warning',
          autoHideDelay: 3000
        });
        return;
      }
      
      try {
        await Requestor.postAsyncP('/api/giftcard/send_email/', {
          gift_card: card,
          recipient_email: card.assigned_to_email,
          recipient_name: card.assigned_to_name || 'Valued Customer',
          store_name: 'Furniture Mecca'
        }, this);
        
        this.$bvToast.toast(`Gift card sent to ${card.assigned_to_email}`, {
          title: 'Success',
          variant: 'success',
          autoHideDelay: 3000
        });
        
        this.loadGiftCards();
      } catch (error) {
        console.error('Error sending email:', error);
        this.$bvToast.toast('Failed to send email', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 3000
        });
      }
    }
  }
};
</script>

<style scoped>
/* Glass Container - Same as Vendors */
.glass-container {
  min-height: 100vh;
  padding: 20px;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  overflow: hidden;
}

.glass-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  z-index: 0;
}

.gift-card-management {
  padding: 20px;
  background: transparent;
  min-height: 100vh;
  z-index: 1;
  position: relative;
}

.modern-card {
  background: rgba(255, 255, 255, 0.7);
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 25px;
  margin: 0 auto;
  margin-top: 10px;
  max-width: 1800px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(240, 240, 240, 0.6);
}

.title-section .page-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  color: #667eea;
}

.title-section .page-subtitle {
  color: #718096;
  font-size: 0.95rem;
  margin: 5px 0 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px 16px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.add-btn {
  background: linear-gradient(135deg, #48bb78, #38a169);
  border: none;
  color: white;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
}

.refresh-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Stats Row */
.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  cursor: default;
  animation: slideUp 0.6s ease forwards;
  opacity: 0;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.8);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
  color: white;
  font-size: 1.4rem;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.8rem;
  color: #718096;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  opacity: 0;
}

/* Filters */
.filters-row {
  margin-bottom: 15px;
}

.filter-item {
  margin-bottom: 10px;
}

.filter-input {
  background: rgba(255, 255, 255, 0.8);
  border-color: #e2e8f0;
}

.filter-input:focus {
  background: #ffffff;
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.search-btn {
  background: #667eea;
  border: none;
}

.search-btn:hover {
  background: #5a67d8;
}

.filter-select {
  background: rgba(255, 255, 255, 0.8);
  border-color: #e2e8f0;
}

.filter-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

/* Info Alert */
.info-alert {
  background: rgba(13, 110, 253, 0.08);
  border: 1px solid rgba(13, 110, 253, 0.15);
  border-radius: 10px;
  margin-bottom: 20px;
}

.alert-content {
  display: flex;
  align-items: flex-start;
}

.alert-content .bi {
  color: #0d6efd;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Loading */
.loading-container {
  text-align: center;
  padding: 60px 20px;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}

.modern-table {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  overflow: hidden;
}

.modern-table >>> thead th {
  background: rgba(244, 247, 252, 0.6) !important;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  padding: 12px 16px;
}

.modern-table >>> tbody td {
  padding: 12px 16px;
  vertical-align: middle;
  border-color: rgba(226, 232, 240, 0.3);
}

.modern-table >>> tbody tr {
  background: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.modern-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.6) !important;
  transform: scale(1.01);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.active {
  background: #48bb78;
}

.status-dot.sent {
  background: #4299e1;
}

.status-dot.redeemed {
  background: #a0aec0;
}

.status-dot.expired {
  background: #f56565;
}

.status-dot.cancelled {
  background: #f56565;
}

.status-dot.pending {
  background: #ed8936;
}

/* Amount and Balance */
.amount-value,
.balance-value {
  font-weight: 600;
  color: #2d3748;
}

/* Email */
.email-value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #4a5568;
}

.email-icon {
  color: #667eea;
  font-size: 0.9rem;
}

/* Date */
.date-value {
  color: #718096;
  font-size: 0.85rem;
}

/* Action Buttons */
.action-icon-btn {
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.3s ease;
  margin: 0 2px;
}

.action-icon-btn:hover {
  transform: scale(1.1);
}

.action-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Table Footer */
.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  margin-top: 15px;
  border-top: 1px solid rgba(226, 232, 240, 0.4);
}

.pagination-info {
  color: #718096;
  font-size: 0.85rem;
}

.custom-pagination >>> .page-item .page-link {
  background: rgba(255, 255, 255, 0.6);
  border-color: #e2e8f0;
  color: #4a5568;
  border-radius: 6px;
  margin: 0 2px;
  transition: all 0.3s ease;
}

.custom-pagination >>> .page-item.active .page-link {
  background: #667eea;
  border-color: #667eea;
  color: white;
}

.custom-pagination >>> .page-item .page-link:hover {
  background: rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.custom-pagination >>> .page-item.active .page-link:hover {
  background: #5a67d8;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  border-radius: 10px;
  background: rgba(248, 250, 252, 0.5);
  border: 1px dashed rgba(226, 232, 240, 0.6);
}

.empty-state-icon {
  color: #a0aec0;
  margin-bottom: 15px;
}

.empty-state h3 {
  color: #2d3748;
  margin-bottom: 8px;
  font-weight: 600;
}

.empty-state p {
  color: #718096;
  margin-bottom: 15px;
}

.empty-state-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

/* Responsive */
@media (max-width: 1200px) {
  .stats-row [class*="col-"] {
    margin-bottom: 10px;
  }
}

@media (max-width: 768px) {
  .modern-card {
    padding: 15px;
  }
  
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .action-buttons {
    width: 100%;
  }
  
  .action-btn {
    flex: 1;
    justify-content: center;
  }
  
  .stat-card {
    padding: 12px 16px;
  }
  
  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }
  
  .stat-value {
    font-size: 1.2rem;
  }
  
  .table-footer {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  
  .filters-row [class*="col-"] {
    margin-bottom: 10px;
  }
}

@media (max-width: 480px) {
  .action-buttons {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
    justify-content: center;
  }
  
  .stat-card {
    flex-direction: column;
    text-align: center;
  }
  
  .stat-icon {
    margin-right: 0;
    margin-bottom: 8px;
  }
  
  .empty-state-actions {
    flex-direction: column;
  }
  
  .empty-state-actions .btn {
    width: 100%;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/GiftCard/GiftCardManagement.vue